/**
 * ============================================================================
 * analytics.ts — API pública compatible + wrapper sobre dataLayer.ts
 * ============================================================================
 *
 * Este fichero mantiene la MISMA API pública que la versión pre-FASE 0
 * (trackEvent, setLanguage, trackPageView, trackSectionView, trackSectionEngagement)
 * para que los hooks existentes (useLanguageTracking, usePageTracking,
 * useSectionTracking) sigan funcionando SIN CAMBIOS.
 *
 * Por debajo, en vez de llamar a `window.gtag()` directamente, todo se
 * empuja al `window.dataLayer` de forma que Google Tag Manager (contenedor
 * GTM-K4WC3TVM) sea la fuente única de verdad de la orquestación de tags.
 *
 * Para eventos NUEVOS (e-commerce, custom Brickshare) usar directamente
 * las funciones de `dataLayer.ts` (trackEcommerce, trackEvent).
 *
 * GA4 property: G-H511LVQLMX (Brickshare production, configurada como tag
 * dentro de GTM, no como script directo en el HTML).
 * ============================================================================
 */

import {
  trackEvent as pushEvent,
  trackPageView as pushPageView,
  setUserProperties,
  pushDataLayer,
} from "./dataLayer";

export type SupportedLanguage = "es" | "ca" | "en";

/**
 * Devuelve true si el dataLayer está disponible (siempre lo está en el
 * navegador tras la carga de index.html, porque lo inicializa el bloque
 * de Consent Mode v2). Sirve para tests SSR.
 */
export const isAnalyticsReady = (): boolean => {
  return typeof window !== "undefined" && Array.isArray(window.dataLayer);
};

/**
 * Envía un evento arbitrario al dataLayer.
 * Compatible con la firma anterior de gtag('event', name, params).
 *
 * NOTA: para eventos que ya tenemos tipados en dataLayer.ts es mejor
 * importar trackEvent desde ahí (tiene autocompletado). Este wrapper existe
 * solo para no romper el código legacy.
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, unknown> = {}
): void => {
  if (!isAnalyticsReady()) return;
  // Cast al tipo EventName sabiendo que puede ser un evento ad-hoc no tipado
  pushEvent(eventName as never, params);
};

/**
 * Fija el idioma como user_property y emite el evento `language_change`.
 *
 * Cambios respecto a la versión anterior:
 *   - Ya NO llama a gtag('config', ...) para reinyectar el ID de medición
 *     (eso lo hace GTM automáticamente al recibir user_properties).
 *   - Empuja al dataLayer un evento `bs_user_properties_update` que GTM
 *     mapea a "GA4 Event > user_properties" en el tag correspondiente.
 *
 * Llamar cada vez que el usuario cambie de idioma con el selector.
 */
export const setLanguage = (
  lang: SupportedLanguage | string,
  isExplicitChange = false
): void => {
  if (!isAnalyticsReady()) return;
  try {
    // user_property scope: dimensión "Language" en GA4
    setUserProperties({ language: lang });
    // Evento explícito para poder ver los cambios en informes de comportamiento (solo si el usuario lo cambió activamente)
    if (isExplicitChange) {
      pushEvent("language_change", { language: lang });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] setLanguage failed", err);
  }
};

/**
 * Registra manualmente un page_view (SPA).
 * usePageTracking sigue llamándolo en cada cambio de ruta.
 */
export const trackPageView = (path: string, title: string): void => {
  if (!isAnalyticsReady()) return;
  try {
    pushPageView(path, title);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] trackPageView failed", err);
  }
};

/**
 * Se dispara cuando una sección entra al viewport (>= 50% durante >= 1s).
 * useSectionTracking lo llama automáticamente por cada elemento con
 * data-section="...".
 *
 * Nombre técnico en dataLayer: `bs_section_view` (namespace Brickshare).
 * En GA4 aparece como custom event `bs_section_view` con el parámetro
 * `section_name` (registrado como custom dimension "Section Name").
 */
export const trackSectionView = (sectionName: string): void => {
  pushEvent("bs_section_view", {
    section_name: sectionName,
  });
};

/**
 * Se dispara cuando la sección sale del viewport o el usuario navega fuera.
 * Reporta cuánto tiempo ha estado el usuario efectivamente viéndola.
 *
 * Filtro anti-ruido: engagements < 0.5s se descartan (micro-vistas por
 * scroll rápido que ensucian el reporte).
 */
export const trackSectionEngagement = (
  sectionName: string,
  timeSpentMs: number
): void => {
  const seconds = Math.round(timeSpentMs / 100) / 10; // 1 decimal
  if (seconds < 0.5) return;
  pushEvent("bs_section_engagement", {
    section_name: sectionName,
    time_spent_seconds: seconds,
  });
};

/**
 * Utilidad para hacer un ping "raw" al dataLayer sin pasar por los tipos.
 * Solo se debe usar para migraciones puntuales o debugging.
 * @deprecated Usa trackEvent o las funciones tipadas de dataLayer.ts
 */
export const rawPush = (payload: Record<string, unknown>): void => {
  pushDataLayer(payload);
};